// Tanks cuits en images : la caisse, la tourelle et leurs ombres portées sont dessinées une fois par apparence
// (classe × apparence × couleur du camp), ombrage premium compris (shade.ts), puis réutilisées comme sprites par tous
// les tanks qui la portent. Le coût par image reste celui d'un sprite, quelle que soit la richesse du dessin.
import { Container, Graphics, GraphicsContext, type Renderer, type Texture } from 'pixi.js';
import type { ClassNode } from '../../../../shared/catalog/classes';
import { lookKey, type TankLook } from '../../../../shared/cosmetics';
import { drawBarrel } from './art';
import { drawPaintedTank } from './paint';
import { glossCore, shadeBarrels, shadeDome, shadeHull, shadowOf } from './shade';
import { drawTankParts, lookForCode } from './variants';

export interface BakedPart {
  texture: Texture;
  /** Ancre du sprite pour que l'origine du dessin (centre du tank) tombe sur la position du sprite. */
  ax: number;
  ay: number;
}

export interface BakedTank {
  hull: BakedPart;
  turret: BakedPart;
  hullShadow: BakedPart;
  turretShadow: BakedPart;
  /** Tourelles automatiques : géométrie partagée (elles tournent à part, elles restent vectorielles). */
  auto: GraphicsContext;
  hasAuto: boolean;
}

/** Au-delà, le cache est vidé d'un coup (les tanks en cours se recuisent à l'image suivante). */
const MAX = 160;

export class PaintCache {
  private map = new Map<string, BakedTank>();
  /** Incrémenté à chaque vidage : les vues comparent pour savoir qu'il faut recuire. */
  generation = 0;
  /**
   * Textures d'un cache vidé : détruites seulement après le rendu de l'image en cours (flush), car des tanks déjà mis
   * à jour dans cette image les affichent encore.
   */
  private retired: Texture[] = [];

  constructor(private readonly renderer: Renderer) {}

  private bake(c: Container, res: number): BakedPart {
    const b = c.getLocalBounds();
    const texture = this.renderer.generateTexture({ target: c, resolution: res, antialias: true });
    const part = { texture, ax: b.width ? -b.x / b.width : 0.5, ay: b.height ? -b.y / b.height : 0.5 };
    c.destroy({ children: true });
    return part;
  }

  private store(key: string, cls: ClassNode, hull: Container, turret: Container, auto: Graphics, hasAuto: boolean): BakedTank {
    if (this.map.size >= MAX) this.clear();
    // 3 au plus : au-delà, la mémoire graphique grimpe (jusqu'à 160 apparences × 4 textures) sans gain visible.
    const res = Math.min(3, 2 * (globalThis.devicePixelRatio || 1));
    const baked: BakedTank = {
      hull: this.bake(hull, res),
      turret: this.bake(turret, res),
      // Ombres floues : une résolution plus basse suffit.
      hullShadow: this.bake(shadowOf('hull', cls), 1),
      turretShadow: this.bake(shadowOf('turret', cls), 1),
      auto: auto.context,
      hasAuto,
    };
    this.map.set(key, baked);
    return baked;
  }

  /** Tank peint (cosmétiques refaits). */
  get(cls: ClassNode, look: TankLook, accent: number, decor = 0): BakedTank {
    const key = `p|${cls.index}|${accent}|${decor}|${lookKey(look)}`;
    const hit = this.map.get(key);
    if (hit) return hit;
    const t = drawPaintedTank(cls, look, accent, 1, decor);
    return this.store(key, cls, t.hull, t.turret, t.auto, t.hasAuto);
  }

  /** Tank classique (styles de variante et skins de couleur), avec le même ombrage. */
  getClassic(cls: ClassNode, code: number, accent: number, decor: number): BakedTank {
    const key = `c|${cls.index}|${accent}|${code}|${decor}`;
    const hit = this.map.get(key);
    if (hit) return hit;
    const hullG = new Graphics();
    const turretG = new Graphics();
    const auto = new Graphics();
    const hasAuto = drawTankParts({ hull: hullG, turret: turretG, auto }, cls, code, accent, decor);
    const pal = lookForCode(code).palette;
    const hull = new Container();
    hull.addChild(hullG);
    shadeHull(hull, cls.index);
    const turret = new Container();
    turret.addChild(turretG);
    shadeBarrels(turret, cls);
    // Embouchures et trappes à la couleur du camp redessinées au-dessus de l'ombrage.
    const tips = new Graphics();
    for (const b of cls.weapon.barrels) if (!b.auto) drawBarrel(tips, b, pal, accent, true);
    turret.addChild(tips);
    shadeDome(turret);
    glossCore(turret, accent, pal.outline);
    return this.store(key, cls, hull, turret, auto, hasAuto);
  }

  clear(): void {
    for (const b of this.map.values()) for (const p of [b.hull, b.turret, b.hullShadow, b.turretShadow]) this.retired.push(p.texture);
    this.map.clear();
    this.generation++;
  }

  /** À appeler après le rendu : détruit les textures du dernier vidage. */
  flush(): void {
    if (!this.retired.length) return;
    for (const t of this.retired) t.destroy(true);
    this.retired = [];
  }
}
